import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
} from "react";
import { useForm } from "@mantine/form";
import { TextInput, Textarea, MultiSelect } from "@mantine/core";
import { useLocation } from "react-router-dom";

import { UserContext } from "@/contexts/UserContext/UserContext";
import { Validatable } from "./StudyStepper";
import { useDarkModeContext } from "@/hooks/contextHooks/useDarkModeContext";
import { getFormStyles } from "@/helpers/getFormStyles";
import { ProjectDataType } from "@/types/apiTypes";
import styles from "../../css/study.module.css";

type Props = {
  projects: ProjectDataType[];
};

const StudyNameStep = forwardRef<Validatable, Props>(({ projects }, ref) => {
  const context = useContext(UserContext);
  const { state } = useLocation();
  const routeProject = (state as { projectId?: string })?.projectId ?? "";

  const { isDarkMode } = useDarkModeContext();
  const formStyles = getFormStyles(isDarkMode);

  const studyForm = useForm({
    initialValues: {
      studyName: context?.studyFormValues.studyName || "",
      comment: context?.studyFormValues.comment || "",
      selectedProjects: context?.studyFormValues.selectedProjects?.length
        ? context.studyFormValues.selectedProjects // keep previous choice
        : routeProject
          ? [routeProject]
          : [],
    },
    validate: {
      studyName: (value: string) =>
        value.trim() ? null : "Study name is required",
      comment: (value: string) => (value.trim() ? null : "Comment is required"),
    },
  });

  useEffect(() => {
    context?.setStudyFormValues((prev) => ({ ...prev, ...studyForm.values }));
  }, [studyForm.values, context]);

  useEffect(() => {
    if (
      routeProject &&
      !studyForm.values.selectedProjects.includes(routeProject)
    ) {
      studyForm.setFieldValue("selectedProjects", [routeProject]);
      context?.setStudyFormValues((prev) => ({
        ...prev,
        selectedProjects: [routeProject],
      }));
    }
  }, [routeProject]);

  useImperativeHandle(ref, () => ({
    validate: () => !studyForm.validate().hasErrors,
  }));

  return (
    <div className={styles.stepContainer}>
      <form id="study-form-step-0" className={styles.form}>
        <TextInput
          label="Study Name"
          {...studyForm.getInputProps("studyName")}
          withAsterisk
          required
          styles={formStyles}
        />
        <Textarea
          label="Comment"
          {...studyForm.getInputProps("comment")}
          withAsterisk
          required
          styles={formStyles}
        />
        <MultiSelect
          label="Assign to Projects"
          data={projects.map((p) => ({ value: p.id, label: p.name }))}
          value={studyForm.values.selectedProjects}
          onChange={(values) => {
            studyForm.setFieldValue("selectedProjects", values);
            context?.setStudyFormValues((prev) => ({
              ...prev,
              selectedProjects: values,
            }));
          }}
          placeholder="Select projects"
          styles={formStyles}
        />
      </form>
    </div>
  );
});

export default StudyNameStep;
